import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class ApiService {
  private url: string = environment.apiUrl;

  constructor(
    private http: HttpClient
  ) { }

  public getPeriods(indicatorTypeID): Observable<any> {
    return this.http.get(
      this.url + 'timePeriod', {
        params: new HttpParams().set('indicatorTypeID', indicatorTypeID.toString())
      }
    )
  }

  public getOutcomePeriods(indicatorTypeID): Observable<any> {
    return this.http.get(
      this.url + 'timePeriod', {
        params: new HttpParams().set('indicatorTypeID', indicatorTypeID.toString())
      }
    )
  }

  public getSystemLevels(periodId: number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('timePeriodID', periodId.toString());
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'area/systemLevel', {
        params: params
      }
    );
  }

  public getAreas(periodId: number, systemLevelId: number, parentAreaId: number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('timePeriodID', periodId.toString());

    if (systemLevelId) {
      params = params.set('systemLevelID', systemLevelId.toString());
    } else if (parentAreaId) {
      params = params.set('parentAreaID', parentAreaId.toString());
    }
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'area', {
        params: params
      }
    );
  }

  public getSystemLevelTypes(periodId: number): Observable<any> {
    return this.http.get(
      this.url + 'area/systemLevelTypes', {
        params: new HttpParams().set('timePeriodID', periodId.toString())
      }
    );
  }

  public getAreasBySearch(partialAreaName: string, periodId: number, systemLevelTypeId?: number): Observable<any> {
    // This endpoint returns a bare NaN for OdsCode on some PCN and practice rows,
    // which is not valid JSON, so HttpClient's own parse fails and the caller sees
    // an error instead of results. Read the body as text and null those out.
    // Remove once the API returns valid JSON for every row.
    let params: HttpParams = new HttpParams()
      .set('partialAreaName', partialAreaName)
      .set('timePeriodID', periodId.toString());
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'area/search', {
        params: params,
        responseType: 'text'
      }
    ).pipe(
      map(body => JSON.parse(body.replace(/:\s*NaN\s*(?=[,}])/g, ':null')))
    );
  }

  public getUnassignedAreas(periodId: number, systemLevelId?: number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('timePeriodID', periodId.toString());
    if (systemLevelId) {
      params = params.set('systemLevelID', systemLevelId.toString());
    }
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'area/unassigned', {
        params: params
      }
    );
  }

  public getArea(areaId: number, periodId: number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('timePeriodID', periodId.toString());
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'area/' + areaId.toString() + '/details', {
        params: params
      }
    );
  }

  public getIndicators(periodId: number, areaId: number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('timePeriodID', periodId.toString()).set('areaID', areaId.toString());
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'indicator', {
        params: params
      }
    );
  }

  public getIndicatorList(periodId: number, systemLevelId: number): Observable<any> {
    return this.http.get(
      this.url + 'indicator/list', {
        params: new HttpParams().set('timePeriodID', periodId.toString()).set('systemLevelID', systemLevelId.toString())
      }
    );
  }

  public getIndicator(indicatorId: number, periodId: number, areaId: number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('timePeriodID', periodId.toString()).set('areaID', areaId.toString());
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'indicator/' + indicatorId.toString() + '/data', {
        params: params
      }
    );
  }

  public getIndicatorDetails(indicatorId: number): Observable<any> {
    return this.http.get(
      this.url + 'indicator/' + indicatorId.toString() + '/details'
    );
  }

  public getIndicatorTags(): Observable<any> {
    return this.http.get(
      this.url + 'indicator/tags'
    );
  }

  public getSiblingData(periodId: number, areaId: number, metricId: number): Observable<any> {
    return this.http.get(
      this.url + 'indicator/siblingData', {
        params: new HttpParams().set('timePeriodID', periodId.toString()).set('areaID', areaId.toString()).set('metricID', metricId.toString())
      }
    );
  }

  public getChildData(periodId: number, areaId: number, metricId: number): Observable<any> {
    return this.http.get(
      this.url + 'indicator/childData', {
        params: new HttpParams().set('timePeriodID', periodId.toString()).set('areaID', areaId.toString()).set('metricID', metricId.toString())
      }
    );
  }

  public getJsonIndicatorData(indicatorId: number, periodId: number, systemLevelId: number): Observable<any> {
    return this.http.get(
      this.url + 'indicator/' + indicatorId.toString() + '/rawDataJSON', {
        params: new HttpParams().set('timePeriodID', periodId.toString()).set('systemLevelID', systemLevelId.toString())
      }
    );
  }

  public getCsvIndicatorData(indicatorId: number, periodId: number, systemLevelId: number): Observable<any> {
    return this.http.get(
      this.url + 'indicator/' + indicatorId.toString() + '/rawDataCSV', {
        params: new HttpParams().set('timePeriodID', periodId.toString()).set('systemLevelID', systemLevelId.toString())
      }
    );
  }

  public getResourceData(): Observable<any> {
    return this.http.get(
      this.url + 'externalResource'
    );
  }

  // Strategic View

  public getNationalVsAreaMetricData(metricId: number, timePeriodID: number, areaID: number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('timePeriodID', timePeriodID.toString()).set('areaID', areaID.toString());
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'indicator/nationalVsAreaMetricData/' + metricId.toString(), {
        params: params
      }
    );
  }

  public getIndicatorPriorityGroups(): Observable<any> {
    return this.http.get(
      this.url + 'indicator/priorityGroups', {}
    );
  }

  public getIndicatorPathwayGroup(pathwayGroupId: number): Observable<any> {
    return this.http.get(
      this.url + 'indicator/pathwayGroup/' + pathwayGroupId.toString(), {}
    );
  }

  public getIndicatorGroup(indicatorGroupId: number): Observable<any> {
    return this.http.get(
      this.url + 'indicator/indicatorGroup/' + indicatorGroupId.toString(), {}
    );
  }

  public getIndicatorNotifications(indicatorID: number, systemLevelID: number, timePeriodID: number): Observable<any> {
    return this.http.get(
      this.url + 'notifications', {
        params: new HttpParams().set('IndicatorID', indicatorID.toString()).set('SystemLevelID', systemLevelID.toString()).set('TimePeriodID', timePeriodID.toString())
      }
    );
  }

  public getAllPersonsTimeSeries(metric_ID: number, areaID:number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('areaID', areaID.toString());
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'indicator/timeSeriesByMetric/' + metric_ID.toString(), {
          params: params
      }
    );
  }

  public getInequalitiesMarkerTimeSeries(indicator_ID: number, areaID:number): Observable<any> {
    return this.http.get(
      this.url + 'indicator/personsTimeSeriesByIndicator/' + indicator_ID.toString(), {
        params: new HttpParams().set('areaID', areaID.toString())
      }
    );
  }

  public getSystemLevelComparison(metric_ID: number, areaID:number, timePeriodID:number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('areaID', areaID.toString()).set('timePeriodID', timePeriodID.toString());
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'indicator/metricSystemLevelComparison/' + metric_ID.toString(), {
         params: params
      }
    );
  }

  public getAreaBreakDown(metric_ID: number, areaID:number, timePeriodID:number, systemLevelTypeId?: number): Observable<any> {
    let params: HttpParams = new HttpParams().set('areaID', areaID.toString()).set('timePeriodID', timePeriodID.toString());
    if (systemLevelTypeId) {
      params = params.set('systemLevelTypeID', systemLevelTypeId.toString());
    }

    return this.http.get(
      this.url + 'indicator/metricAreaBreakdown/' + metric_ID.toString(), {
        params: params
      }
    );
  }

}
